import type { SignedUrlResponse } from "@/services/storageService.types";

// sessionStorage backing for the signed-URL cache.

const STORAGE_PREFIX = "paribus:signed-url:v2:";
/** Treat a cached URL as expired this long before `expires_at` (clock skew, JWT second boundary). */
export const CLOCK_SKEW_BUFFER_MS = 90_000;

type PersistedSignedUrl = {
  signed_url: string;
  expires_at: string;
};

function storageKey(cacheKey: string): string {
  return `${STORAGE_PREFIX}${cacheKey}`;
}

function parsePayload(raw: string): PersistedSignedUrl | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as PersistedSignedUrl).signed_url === "string" &&
      typeof (parsed as PersistedSignedUrl).expires_at === "string"
    ) {
      return parsed as PersistedSignedUrl;
    }
  } catch {
    /* malformed payload */
  }
  return null;
}

function isStillValid(payload: PersistedSignedUrl): boolean {
  const expiresAtMs = Date.parse(payload.expires_at);
  if (!Number.isFinite(expiresAtMs)) {
    return false;
  }
  return Date.now() < expiresAtMs - CLOCK_SKEW_BUFFER_MS;
}

export function readPersistedSignedUrl(cacheKey: string): PersistedSignedUrl | null {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  const key = storageKey(cacheKey);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const payload = parsePayload(raw);
    if (!payload || !isStillValid(payload)) {
      sessionStorage.removeItem(key);
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

export function writePersistedSignedUrl(cacheKey: string, data: SignedUrlResponse): void {
  if (typeof sessionStorage === "undefined" || !data.signed_url || !data.expires_at) {
    return;
  }
  try {
    const payload: PersistedSignedUrl = {
      signed_url: data.signed_url,
      expires_at: data.expires_at,
    };
    sessionStorage.setItem(storageKey(cacheKey), JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

export function removePersistedSignedUrl(cacheKey: string): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(storageKey(cacheKey));
  } catch {
    /* private mode */
  }
}

export function clearAllPersistedSignedUrls(): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* private mode */
  }
}
